import mainStyle from "../../../mainStyle.module.scss";

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  TextField,
  Typography,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { v4 as uuidv4 } from "uuid";
import { cloneDeep } from 'lodash';

import { formLimitation } from "const";
import CollapseCard from "components/Collapse";
import { MenuSelect } from "components/Select/menuSelect";
import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { RadioButton } from 'components/buttons/RadioButton';
import { Loading } from 'components/Loading';
import MacWhitelistSection from "../components/macWhitelistSection";
import RadiusProfileSection from "../components/radiusProfileSection";
import AggregationConfirmModal from '../Modal/AggregationConfirmModal';
import LeaveConfirmModal from "components/Drawer/Content/Settings/Profiles/Port/leaveConfirmModal";
import LinkModal from "components/LinkModal";
import useZustandStore from "zustandStore";
import useAuthScope from "hooks/useAuthScope";
import icon_snmp from "assets/images/icons/icn_Info_solid.svg";
import icon_snmp_hover from "assets/images/icons/icn_info_solid_hover.svg";
import { ReactComponent as ProColorIcon } from "assets/images/icons/icn-pro.svg";
import { ReactComponent as ProOrangeOutlineIcon } from "assets/images/icons/icn_Pro_orange_outline.svg";

// Helper
import {
  getPortMediaType,
  getUpdatedAssociatedPorts,
  checkPortInOtherAggregateGroup,
} from "./helper";
import { getAvailableAggregateGroupId } from "../../helper";

// Const
import {
  defaultAccessPolicyTypeList,
  accessPolicyTypeDefinition,
  defaultOperationList,
  operationDefinition
} from "../../../const";

const rowStyle = {
  display: "flex",
  gap: 3,
  alignItems: "center",
  width: "100%",
};

const proIconStyle = {
  display: "inline-block",
  verticalAlign: "middle",
  marginLeft: 8,
  marginTop: -12,
  marginBottom: -12,
  flexShrink: 0,
};

const legendItemStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  fontSize: "12px",
  color: "text.secondary"
};

const portBoxStyle = (status, theme) => {
  const base = {
    width: "28px",
    height: "28px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
    boxSizing: "border-box",
  };

  if (status === "source") {
    return {
      ...base,
      backgroundColor: theme?.palette?.primary?.blueSecound || "#87A2E2",
      color: "#ffffff",
      cursor: "default",
    };
  }
  if (status === "destination" || status === "selected") {
    return {
      ...base,
      backgroundColor: theme?.palette?.primary?.main || "#172664",
      color: "#ffffff",
    };
  }
  if (status === "disabled") {
    return {
      ...base,
      backgroundColor: "#e9ecef",
      color: "#adb5bd",
      opacity: 0.6,
      cursor: "not-allowed",
    };
  }
  return {
    ...base,
    backgroundColor: "#ffffff",
    color: theme?.palette?.primary?.main || "#172664",
    border: "1px solid #DDDDDD",
    "&:hover": {
      borderColor: "#909399",
      backgroundColor: "#ebedf0",
    }
  };
};

const checkPvid = (value) => {
  if (value === "") {
    return false;
  }

  if (!/^\d+$/.test(value)) {
    return false;
  }

  const intValue = Number(value);
  if (intValue < 1 || intValue > 4094) {
    return false;
  }

  return true;
};

const checkAllowVlans = (value) => {
  if (!value?.trim()) return false;

  const parts = value.split(",").map((s) => s.trim());
  const used = new Set();
  const { min, max } = formLimitation.vlanId;

  for (let part of parts) {
    if (part === "") return false;

    if (part.includes("-")) {
      const [startStr, endStr] = part.split("-").map((s) => s.trim());
      const start = parseInt(startStr, 10);
      const end = parseInt(endStr, 10);

      if (isNaN(start) || isNaN(end) || start < min || end > max || start > end)
        return false;

      for (let i = start; i <= end; i++) {
        if (used.has(i)) return false;
        used.add(i);
      }
    } else {
      const num = parseInt(part, 10);
      if (isNaN(num) || num < min || num > max) return false;

      if (used.has(num)) return false;
      used.add(num);
    }
  }

  return true;
};

const SettingsDrawer = ({
  drawerOpen,
  setAlertInfo,
  linkSpeedOptions,
  activeRow,
  setActiveRow,
  originalRowData,
  setOriginalRowData,
  switchesData,
  setSwitchesData,
  onUpdateBlockerData,
  profileSwitchList,
  getSupportedSpeedOptions,
  showEditGeneral,
  isSelectedPortSupportPoE,
  vlanIdsCount,
  scheduleOptions,
  radiusProfiles,
  macProfiles,
  onGoToPage
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { auth } = useAuthScope();
  const isUnityProSubscribed = useZustandStore((state) => state.isUnityProSubscribed);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const isProfileMode = useMemo(() => !selectedProfile?.isDefault, [selectedProfile?.isDefault]);

  const [leaveModalState, setLeaveModalState] = useState({
    open: false,
    profileType: "",
    page: ""
  });

  const handleLeaveConfirm = () => {
    setLeaveModalState(prev => ({ ...prev, open: false }));
    onGoToPage?.("profiles", leaveModalState.page);
  };
  const { isCopper, isFiber, linkSpeedLabel } = useMemo(() => getPortMediaType({ activeRow }), [activeRow]);

  const linkSpeedData = useMemo(
    () => linkSpeedOptions.sort((a, b) => a.id - b.id),
    [linkSpeedOptions]
  );

  const [isLoading, setIsLoading] = useState(false);
  const [updateUuid, setUpdateUuid] = useState(uuidv4());
  const [isChanged, setIsChanged] = useState(false);
  const isEditable = useMemo(() => !selectedProfile || selectedProfile?.isDefault, [selectedProfile]);
  const [showAggregationConfirmModal, setShowAggregationConfirmModal] = useState(false);

  const devicePorts = useMemo(() => {
    if (!activeRow?.macAddress) return [];
    return switchesData
      .filter((p) => p.macAddress === activeRow.macAddress)
      .sort((a, b) => a.portIndex - b.portIndex);
  }, [switchesData, activeRow?.macAddress]);

  useEffect(() => {
    if (activeRow?.operation?.type === "aggregation" && activeRow?.operation?.aggregateGroupId == null && activeRow?.macAddress) {
      const groupId = getAvailableAggregateGroupId(switchesData, activeRow.macAddress, null, activeRow.aggregation);
      setActiveRow(prev => ({
        ...prev,
        operation: {
          ...prev.operation,
          aggregateGroupId: groupId,
          aggregateType: (prev.operation?.aggregateType || "lacp").toLowerCase()
        }
      }));
    }
  }, [activeRow?.operation?.type, activeRow?.macAddress, switchesData]);

  const isPortInOtherAggregateGroup = (portItem) => {
    return checkPortInOtherAggregateGroup({
      portItem,
      macAddress: activeRow.macAddress,
      selectedIndexList: [+activeRow.portIndex],
      switchesData
    });
  };

  const supportedSpeed = getSupportedSpeedOptions(
    activeRow.linkSpeed,
    activeRow.portIndex
  );
  const breadcrumbs = JSON.parse(localStorage.getItem("selectBreadcrumbs"));
  const hasManageOrg = breadcrumbs.realm?.hasManageOrg === true;
  const realmId = breadcrumbs.realm?.id ?? null;
  const orgsId = breadcrumbs.orgs?.id ?? null;
  const siteId = breadcrumbs.site?.id ?? null;
  let entId = null;
  let orgId = null;

  if (hasManageOrg) {
    entId = realmId;
    if (orgsId) orgId = orgsId;
  } else {
    orgId = realmId;
  }

  const isValidInputStyles = (isValid) => ({
    width: "64.5%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.borderGreyPrimary
          : theme.palette.primary.redPrimary,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.mainHover
          : theme.palette.primary.redPrimary,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.main
          : theme.palette.primary.redPrimary,
      },
    },
  });

  const checkFormValid = () => {
    if (
      activeRow.portType === "trunk" &&
      !checkAllowVlans(activeRow.allowVlan?.trim())
    ) {
      return false;
    }

    if (!checkPvid(activeRow.PVID?.toString().trim())) {
      return false;
    }

    if (vlanIdsCount.number > 128) {
      return false;
    }

    if (supportedSpeed.s.length > 0 && activeRow.speedS == null) {
      return false;
    }

    if (supportedSpeed.t.length > 0 && activeRow.speedT == null) {
      return false;
    }

    if (activeRow.isPortScheduleMode && !activeRow.portSchedule) {
      return false;
    }

    if (activeRow.isPoeScheduleMode && !activeRow.poeSchedule) {
      return false;
    }

    if (activeRow.operation?.type === "mirroring") {
      if (!activeRow.operation.mirrorPort || activeRow.operation.mirrorPort.length === 0) {
        return false;
      }

      // Restrict to at most one mirror group per switch device
      const activeIdx = +activeRow.portIndex;
      const otherMirrorPorts = switchesData
        .filter(p => p.macAddress === activeRow.macAddress && +p.portIndex !== activeIdx && p.operation?.type === "mirroring")
        .map(p => +p.portIndex);

      if (otherMirrorPorts.length > 0 && originalRowData.operation?.type !== "mirroring") {
        const newMirrorPorts = activeRow.operation.mirrorPort || [];
        const hasIntersection = newMirrorPorts.some(port => otherMirrorPorts.includes(port));
        if (!hasIntersection) {
          return false;
        }
      }
    }

    if (activeRow.operation?.type === "aggregation") {
      if (!activeRow.operation.aggregatePort || activeRow.operation.aggregatePort.length === 0) {
        return false;
      }

      // aggregateGroupId 為 null 代表這台裝置已經沒有可用的 Aggregation 群組了
      // (getAvailableAggregateGroupId 在 capabilities.aggregation 額度用完時會回傳 null).
      if (activeRow.operation.aggregateGroupId == null) {
        return false;
      }

      // capabilities.aggregation 同時也是「每組最多幾個 port」的上限,
      // 群組成員數 = 自己這個 port + aggregatePort 裡的成員數.
      const maxAggregationPorts = activeRow.aggregation;
      if (maxAggregationPorts != null && (activeRow.operation.aggregatePort.length + 1) > maxAggregationPorts) {
        return false;
      }
    }

    if (activeRow.portType === "access" && activeRow.accessPolicy?.enableState) {
      const { type, macList, radiusProfile, dynamicSize } = activeRow.accessPolicy;

      if (type === "STATIC") {
        if (!macList) return false;
      } else if (type === "DELETE_ON_TIME") {
        if (!macList) return false;
        if (dynamicSize === "" || dynamicSize == null) return false;
        const sizeNum = Number(dynamicSize);
        if (isNaN(sizeNum) || sizeNum < 1 || sizeNum > 32) return false;
      } else if (type === "8021X_PROFILE") {
        if (!radiusProfile) return false;
      }
    }

    return true;
  };

  const handleCancel = () => {
    setActiveRow(originalRowData);

    const matchedProfile = profileSwitchList.find(profile => profile.value === originalRowData.portProfile) || null;
    setSelectedProfile(matchedProfile);
  };

  const handleSaveClick = () => {
    if (!isUnityProSubscribed) {
      const isAccessPort = isProfileMode
        ? selectedProfile?.portType === "access"
        : activeRow?.portType === "access";
      const isAccessPolicyEnabled = isProfileMode
        ? selectedProfile?.accessPolicy?.enableState === true
        : activeRow?.accessPolicy?.enableState === true;

      if (isAccessPort && isAccessPolicyEnabled) {
        setIsLinkModalOpen(true);
        return;
      }
    }

    const opType = isProfileMode ? selectedProfile?.operation?.type : activeRow.operation?.type;
    if (!isProfileMode && opType === "aggregation") {
      setShowAggregationConfirmModal(true);
    } else {
      onSave();
    }
  };

  const onSave = () => {
    const updatedPort = getUpdatedPort();

    const newSwitchPortsData = getUpdatedAssociatedPorts({
      switchesData,
      activeRow,
      originalRowData,
      updatedPort
    });

    setSwitchesData(cloneDeep(newSwitchPortsData));
    onUpdateBlockerData(newSwitchPortsData);

    const newlyUpdatedActivePort = newSwitchPortsData.find(p => p.id === activeRow.id);
    if (newlyUpdatedActivePort) {
      setActiveRow(newlyUpdatedActivePort);
      setOriginalRowData(cloneDeep(newlyUpdatedActivePort));
    } else {
      setOriginalRowData(cloneDeep(activeRow));
    }

    setIsChanged(false);

    setAlertInfo({
      open: true,
      severity: "success",
      message: (
        <Box>
          <Box
            sx={{
              fontSize: theme.typography.customFontSize[3],
              fontWeight: theme.typography.customFontWeight[3],
              pb: 1
            }}
          >
            Changes Saved
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Typography>Settings saved to draft. Click</Typography>
            <Typography sx={{ fontWeight: theme.typography.customFontWeight[3] }}> "Apply Changes" </Typography>
            <Typography>to deploy them to the device.</Typography>
          </Box>
        </Box>
      )
    });

  };

  const getUpdatedPort = () => {
    const {
      finalSpeed,
      finalComboSfpSpeed
    } = parseRowLinkSpeed();

    const updatedPort = {
      ...activeRow,
      portProfile: selectedProfile ? selectedProfile.value : "",
      speed: finalSpeed,
      comboSfpSpeed: finalComboSfpSpeed,
      operation: {
        type: activeRow.operation?.type,
        mirrorPort: activeRow.operation?.mirrorPort ?? [],
        mirrorMode: activeRow.operation?.mirrorMode ?? "",
        aggregatePort: activeRow.operation?.aggregatePort ?? [],
        aggregateType: activeRow.operation?.aggregateType ?? "",
        aggregateGroupId: activeRow.operation?.aggregateGroupId ?? null,
      },
    }

    return updatedPort;
  };

  const parseRowLinkSpeed = () => {
    // --- Speed 分流 (S/T) ---
    const {
      s: sSpeeds = [],
      t: tSpeeds = []
    } = supportedSpeed || {};

    // 最終 speed（回傳給後端）
    const finalSpeed = sSpeeds.length > 0 ? activeRow.speedS || "" : tSpeeds.length > 0 ? activeRow.speedT || "" : activeRow.speed || "";

    // 若同時支援 S + T，則 T 塞到 comboSfpSpeed
    const finalComboSfpSpeed = sSpeeds.length > 0 && tSpeeds.length > 0 ? activeRow.speedT || "" : undefined;

    return {
      finalSpeed,
      finalComboSfpSpeed
    };
  };

  useEffect(() => {
    if (!drawerOpen) {
      setSelectedProfile(null);
    }
  }, [drawerOpen]);

  useEffect(() => {
    const matchedProfile = profileSwitchList.find(profile => profile.value === activeRow.portProfile) || null;
    setSelectedProfile(matchedProfile);
  }, [activeRow?.portIndex, activeRow?.portProfile]);

  useEffect(() => {
    const changed =
      JSON.stringify(activeRow ?? {}) !== JSON.stringify(originalRowData ?? {});
    setIsChanged(changed);
  }, [activeRow, originalRowData]);

  return (
    <>
      {isLoading && <Loading />}

      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          py: 2,
          px: 4,
          boxSizing: "border-box",
          gap: "20px",
        }}
      >
        <CollapseCard title="Port">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {activeRow && (
              <>
                {/* Port Name */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Name
                  </Typography>
                  <TextField
                    type="text"
                    value={activeRow.portName}
                    /* disabled={true} */ // Port Name is read-only, when API support update, remove this line
                    onChange={(e) =>
                      setActiveRow({
                        ...activeRow,
                        portName: e.target.value,
                      })
                    }
                    sx={isValidInputStyles}
                  />
                </Box>

                {/* Port Profile */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Profile
                  </Typography>
                  <MenuSelect
                    value={selectedProfile?.value || ""}
                    disabled={!showEditGeneral}
                    onChange={(opt) => {
                      const profile = profileSwitchList.find((item) => item.value === opt.value) || null;

                      if (profile?.isDefault) {
                        setActiveRow((prev) => ({
                          ...prev,
                          portProfile: profile ? profile.value : "",
                          portProfileName: profile ? profile.name : "",
                        }))
                      } else {
                        setActiveRow((prev) => ({
                          ...prev,
                          portProfile: profile ? profile.value : "",
                          portProfileName: profile ? profile.name : "",
                        }))
                      }

                      setSelectedProfile(profile);

                      if (profile && !profile.isDefault) {
                        Object.keys(profile).forEach((key) => {
                          if (key !== "name" && key !== "deviceCategory") {
                            if (key === "sfpSpeed") {
                              setActiveRow((prev) => ({
                                ...prev,
                                portProfile: profile ? profile.value : "",
                                portProfileName: profile ? profile.name : "",
                                comboSfpSpeed: profile[key],
                                isPortScheduleMode: !!(profile.portState && profile.portSchedule),
                                isPoeScheduleMode: !!(profile.poeState && profile.poeSchedule),
                              }))
                            } else {
                              setActiveRow((prev) => ({
                                ...prev,
                                portProfile: profile ? profile.value : "",
                                portProfileName: profile ? profile.name : "",
                                isPortScheduleMode: !!(profile.portState && profile.portSchedule),
                                isPoeScheduleMode: !!(profile.poeState && profile.poeSchedule),
                                [key]: profile[key]
                              }))
                            }
                          }
                        });
                      }
                    }}
                    options={profileSwitchList}
                    width="calc(100% - 185px)"
                  />
                </Box>
                {/* Port State */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port State
                  </Typography>

                  {
                    isProfileMode &&
                    <RadioGroup
                      row
                      disabled
                      value={selectedProfile?.isPortScheduleMode ? "schedule" : (selectedProfile?.portState ? "enable" : "disable")}
                      sx={{
                        display: "flex",
                        gap: "12px",
                      }}
                    >
                      {["Enable", "Disable", "Schedule"].map((option, idx) => (
                        <RadioButton
                          key={"profilePortStateRadioButton" + idx}
                          label={option}
                          name="profilePortStateRadio"
                          value={option.toLowerCase()}
                          disabled
                        />
                      ))}
                    </RadioGroup>
                  }

                  {
                    !isProfileMode &&
                    <RadioGroup
                      row
                      value={activeRow?.isPortScheduleMode ? "schedule" : (activeRow?.portState ? "enable" : "disable")}
                      onChange={(e) => {
                        setActiveRow((prev) => ({
                          ...prev,
                          portState: e.target.value === "enable",
                          isPortScheduleMode: e.target.value === "schedule"
                        }));
                      }}
                      sx={{
                        display: "flex",
                        gap: "12px",
                      }}
                    >
                      {["Enable", "Disable", "Schedule"].map((option, idx) => (
                        <RadioButton
                          key={"portStateRadioButton" + idx}
                          label={option}
                          name="portStateRadio"
                          value={option.toLowerCase()}
                          disabled={!showEditGeneral || !isEditable}
                        />
                      ))}
                    </RadioGroup>
                  }
                </Box>
                {isProfileMode && selectedProfile?.isPortScheduleMode && (
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]}></Typography>
                    <MenuSelect
                      disabled
                      value={selectedProfile?.portSchedule || ""}
                      options={scheduleOptions}
                      width="100%"
                    />
                  </Box>
                )}
                {!isProfileMode && activeRow?.isPortScheduleMode && (
                  <Box sx={rowStyle}>
                    <Typography className={mainStyle["collapse-typography"]}></Typography>
                    <MenuSelect
                      disabled={!showEditGeneral || !isEditable}
                      value={activeRow?.portSchedule || ""}
                      onChange={(opt) => {
                        if (opt.value === "Create New Schedule") {
                          setLeaveModalState({ open: true, profileType: "Schedule", page: "schedule" });
                          return;
                        }
                        setActiveRow((prev) => ({
                          ...prev,
                          portSchedule: opt.value,
                        }));
                      }}
                      options={scheduleOptions}
                      placeholder="Select a Schedule Profile"
                      width="100%"
                      pinningOptions={[
                        {
                          value: "Create New Schedule",
                          label: "Create New Schedule",
                          showArrow: true,
                        }
                      ]}
                    />
                  </Box>
                )}
                {/* Port Type */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Port Type
                  </Typography>

                  {
                    isProfileMode &&
                    <RadioGroup
                      row
                      disabled
                      value={selectedProfile?.portType === "access" ? "access" : "trunk"}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Access", "Trunk"].map((option, idx) => (
                        <RadioButton
                          key={"profilePortTypeRadioButton" + idx}
                          label={option}
                          name="profilePortTypeRadio"
                          value={option.toLowerCase()}
                          disabled
                        />
                      ))}
                    </RadioGroup>
                  }

                  {
                    !isProfileMode &&
                    <RadioGroup
                      row
                      value={
                        activeRow?.portType === "access" ? "access" : "trunk"
                      }
                      onChange={(e) => {
                        const newValue = e.target.value;
                        setActiveRow((prev) => ({ ...prev, portType: newValue }));
                      }}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "80px auto",
                        columnGap: "20px",
                      }}
                    >
                      {["Access", "Trunk"].map((option, idx) => (
                        <RadioButton
                          key={"portTypeRadioButton" + idx}
                          label={option}
                          name="portTypeRadio"
                          value={option.toLowerCase()}
                          disabled={!showEditGeneral || !isEditable}
                        />
                      ))}
                    </RadioGroup>
                  }
                </Box>

                {/* PVID */}
                <Box sx={rowStyle}>
                  <Typography
                    className={mainStyle["collapse-typography"]}
                    sx={{ alignSelf: "flex-start", pt: "6px" }}
                  >
                    PVID
                  </Typography>

                  {
                    isProfileMode &&
                    <TextField
                      disabled
                      type="number"
                      value={selectedProfile?.PVID ?? ""}
                      sx={isValidInputStyles(true)}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }

                  {
                    !isProfileMode &&
                    <TextField
                      disabled={!showEditGeneral || !isEditable}
                      type="number"
                      value={activeRow.PVID ?? ""}
                      onChange={(e) =>
                        setActiveRow({
                          ...activeRow,
                          PVID:
                            e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                      sx={isValidInputStyles(
                        checkPvid(activeRow.PVID?.toString().trim())
                      )}
                      helperText="(1~4094)"
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  }
                </Box>

                {
                  !isProfileMode &&
                  vlanIdsCount.number > 128 &&
                  <>
                    <Box
                      sx={{
                        ...rowStyle,
                        marginTop: "-1.3rem"
                      }}
                    >
                      <Typography
                        className={mainStyle["collapse-typography"]}
                        sx={{ alignSelf: "flex-start", pt: "6px" }}
                      >
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.primary.redPrimary,
                          fontSize: "0.75rem",
                        }}
                      >
                        Limit reached: Maximum 128 VLANs allowed.
                      </Typography>

                    </Box>
                  </>
                }

                {/* Allow VLAN */}
                {
                  selectedProfile?.portType === "trunk" &&
                  isProfileMode &&
                  <Box sx={rowStyle}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                      Allow VLAN
                    </Typography>

                    <TextField
                      disabled
                      type="text"
                      placeholder="e.g. 1,4-7"
                      value={selectedProfile?.allowVlan}
                      sx={isValidInputStyles(true)}
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />

                  </Box>
                }

                {
                  activeRow.portType === "trunk" &&
                  !isProfileMode &&
                  <Box sx={rowStyle}>
                    <Typography
                      className={mainStyle["collapse-typography"]}
                      sx={{ alignSelf: "flex-start", pt: "6px" }}
                    >
                      Allow VLAN
                    </Typography>

                    <TextField
                      disabled={!showEditGeneral || !isEditable}
                      type="text"
                      placeholder="e.g. 1,4-7"
                      value={activeRow.allowVlan}
                      onChange={(e) =>
                        setActiveRow({
                          ...activeRow,
                          allowVlan: e.target.value,
                        })
                      }
                      sx={isValidInputStyles(
                        checkAllowVlans(activeRow.allowVlan?.trim())
                      )}
                      helperText={`(${formLimitation.vlanId.min}~${formLimitation.vlanId.max})`}
                      FormHelperTextProps={{
                        sx: { marginLeft: 0, fontSize: 12, color: "gray" },
                      }}
                    />
                  </Box>
                }

                {
                  !isProfileMode &&
                  vlanIdsCount.number > 128 &&
                  <>
                    <Box
                      sx={{
                        ...rowStyle,
                        marginTop: "-1.3rem"
                      }}
                    >
                      <Typography
                        className={mainStyle["collapse-typography"]}
                        sx={{ alignSelf: "flex-start", pt: "6px" }}
                      >
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.primary.redPrimary,
                          fontSize: "0.75rem",
                        }}
                      >
                        Limit reached: Maximum 128 VLANs allowed.
                      </Typography>

                    </Box>
                  </>
                }

                {/* Port Speed */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: 2,
                  }}
                >

                  {/* 上排：s ( Fiber )*/}
                  {
                    isFiber &&
                    (
                      <>
                        {
                          isProfileMode &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled
                              value={selectedProfile?.sfpSpeed}
                              options={linkSpeedOptions}
                              width="100%"
                            />
                          </Box>
                        }
                        {
                          !isProfileMode && supportedSpeed.s.length > 0 &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled={!showEditGeneral || !isEditable}
                              value={activeRow.speedS || ""}
                              onChange={(opt) =>
                                setActiveRow({
                                  ...activeRow,
                                  speedS: opt.value,
                                })
                              }
                              options={supportedSpeed.s}
                              width="100%"
                            />
                          </Box>
                        }
                      </>
                    )
                  }

                  {/* 下排：t ( Copper ) */}
                  {
                    isCopper && (
                      <>
                        {
                          isProfileMode &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled
                              value={selectedProfile?.speed}
                              options={linkSpeedOptions}
                              width="100%"
                            />
                          </Box>
                        }

                        {
                          !isProfileMode && supportedSpeed.t.length > 0 &&
                          <Box sx={rowStyle}>
                            <Typography className={mainStyle["collapse-typography"]}>
                              {/* {`${linkSpeedLabel}Link Speed`} */}
                              Port Speed
                            </Typography>
                            <MenuSelect
                              disabled={!showEditGeneral || !isEditable}
                              value={activeRow.speedT || ""}
                              onChange={(opt) =>
                                setActiveRow({
                                  ...activeRow,
                                  speedT: opt.value,
                                })
                              }
                              options={supportedSpeed.t}
                              width="100%"
                            />
                          </Box>
                        }
                      </>
                    )
                  }
                </Box>

                {/* Operation */}
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Operation
                  </Typography>
                  {isProfileMode && (
                    <MenuSelect
                      disabled
                      value={selectedProfile?.operation?.type || "switching"}
                      options={defaultOperationList}
                      width="100%"
                    />
                  )}
                  {!isProfileMode && (
                    <MenuSelect
                      disabled={!showEditGeneral || !isEditable}
                      value={activeRow.operation?.type || "switching"}
                      onChange={(opt) => {
                        const newType = opt.value;
                        const newGroupId = newType === "aggregation"
                          ? getAvailableAggregateGroupId(switchesData, activeRow.macAddress, activeRow.operation?.aggregateGroupId, activeRow.aggregation)
                          : null;
                        setActiveRow((prev) => ({
                          ...prev,
                          operation: {
                            ...prev.operation,
                            type: newType,
                            mirrorPort: newType === "mirroring" ? (prev.operation?.mirrorPort || []) : [],
                            mirrorMode: newType === "mirroring" ? (prev.operation?.mirrorMode || "both") : "",
                            aggregatePort: newType === "aggregation"
                              ? (prev.operation?.aggregatePort || []).map(Number).filter(idx => idx !== prev.portIndex)
                              : [],
                            aggregateType: newType === "aggregation"
                              ? (["lacp", "static"].includes((prev.operation?.aggregateType || "").toLowerCase())
                                ? prev.operation.aggregateType.toLowerCase()
                                : "lacp")
                              : "",
                            aggregateGroupId: newGroupId
                          }
                        }));
                      }}
                      options={defaultOperationList}
                      width="100%"
                    />
                  )}
                </Box>

                {/* Mirroring Port Selector Grid */}
                {!isProfileMode && activeRow.operation?.type === "mirroring" && (
                  <Box sx={{ mt: 0, pl: "164px", width: "100%", boxSizing: "border-box", mb: 2.5 }}>
                    {/* Legend */}
                    <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: "#ffffff", border: "1px solid #DDDDDD", borderRadius: "2px" }} />
                        <Typography variant="caption">Unselected</Typography>
                      </Box>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.main, borderRadius: "2px" }} />
                        <Typography variant="caption">Source Port</Typography>
                      </Box>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.blueSecound, borderRadius: "2px" }} />
                        <Typography variant="caption">Destination Port</Typography>
                      </Box>
                    </Box>
                    {/* Grid */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 2 }}>
                      {devicePorts.map((p) => {
                        const portIdx = p.portIndex;
                        let status = "unselected";
                        if (portIdx === activeRow.portIndex) {
                          status = "source";
                        } else if ((activeRow.operation?.mirrorPort || []).map(Number).includes(portIdx)) {
                          status = "destination";
                        }

                        return (
                          <Box
                            key={portIdx}
                            sx={portBoxStyle(status, theme)}
                            onClick={() => {
                              if (!showEditGeneral || !isEditable) return;
                              if (status === "source") return;
                              const currentMirror = (activeRow.operation?.mirrorPort || []).map(Number);
                              let updatedMirror;
                              if (status === "destination") {
                                updatedMirror = currentMirror.filter(id => id !== portIdx);
                              } else {
                                updatedMirror = [...currentMirror, portIdx].sort((a, b) => a - b);
                              }
                              setActiveRow((prev) => ({
                                ...prev,
                                operation: {
                                  ...prev.operation,
                                  mirrorPort: updatedMirror
                                }
                              }));
                            }}
                          >
                            {portIdx}
                          </Box>
                        );
                      })}
                    </Box>
                    {/* Mirror Mode Dropdown */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Mirror Mode</Typography>
                      <MenuSelect
                        disabled={!showEditGeneral || !isEditable}
                        value={activeRow.operation?.mirrorMode || "both"}
                        onChange={(opt) => {
                          setActiveRow((prev) => ({
                            ...prev,
                            operation: {
                              ...prev.operation,
                              mirrorMode: opt.value
                            }
                          }));
                        }}
                        options={[
                          { label: "Tx", value: "egress" },
                          { label: "Rx", value: "ingress" },
                          { label: "Both", value: "both" }
                        ]}
                        width="150px"
                      />
                    </Box>
                  </Box>
                )}

                {/* Aggregation Port Selector Grid */}
                {!isProfileMode && activeRow.operation?.type === "aggregation" && (
                  <Box sx={{ mt: 0, pl: "164px", width: "100%", boxSizing: "border-box", mb: 2.5 }}>
                    {/* Legend */}
                    <Box sx={{ display: "flex", gap: 2, mb: 1.5, flexWrap: "wrap" }}>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: "#ffffff", border: "1px solid #DDDDDD", borderRadius: "2px" }} />
                        <Typography variant="caption">Unselected</Typography>
                      </Box>
                      <Box sx={legendItemStyle}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: theme?.palette?.primary?.main || "#172664", borderRadius: "2px" }} />
                        <Typography variant="caption">Selected</Typography>
                      </Box>
                    </Box>
                    {/* Grid */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 0.5 }}>
                      {(() => {
                        // 沒有 aggregateGroupId 代表組數額度已滿, 這個 port 沒辦法建立新群組,
                        // 整個選取格都要鎖住 (跟「這個群組本身已滿員」是不同狀況).
                        const isNoGroupAvailable = activeRow.operation?.aggregateGroupId == null;
                        const maxAggregationPorts = activeRow.aggregation;
                        const currentGroupSize = (activeRow.operation?.aggregatePort || []).length + 1;
                        const isGroupFull = maxAggregationPorts != null && currentGroupSize >= maxAggregationPorts;

                        return devicePorts.map((p) => {
                        const portIdx = p.portIndex;
                        const isSourcePort = portIdx === activeRow.portIndex;
                        const isSelectedInGrid = (activeRow.operation?.aggregatePort || []).map(Number).includes(portIdx);
                        const isOtherAggregated = isPortInOtherAggregateGroup(p);
                        let status = "unselected";
                        if (isSourcePort || isSelectedInGrid) {
                          status = "selected";
                        } else if (isNoGroupAvailable) {
                          status = "noGroup";
                        } else if (isOtherAggregated) {
                          status = "disabled";
                        } else if (isGroupFull) {
                          status = "full";
                        }

                        const renderBox = (
                          <Box
                            key={portIdx}
                            sx={portBoxStyle(status === "full" || status === "noGroup" ? "disabled" : status, theme)}
                            onClick={() => {
                              if (!showEditGeneral || !isEditable) return;
                              if (status === "disabled" || status === "full" || status === "noGroup" || isSourcePort) return;
                              const currentPorts = (activeRow.operation?.aggregatePort || []).map(Number).filter(id => id !== activeRow.portIndex);
                              let updatedPorts;
                              if (isSelectedInGrid) {
                                updatedPorts = currentPorts.filter((id) => id !== portIdx);
                              } else {
                                updatedPorts = [...currentPorts, portIdx].sort((a, b) => a - b);
                              }
                              setActiveRow((prev) => ({
                                ...prev,
                                operation: {
                                  ...prev.operation,
                                  aggregatePort: updatedPorts
                                }
                              }));
                            }}
                          >
                            {portIdx}
                          </Box>
                        );

                        if (status === "disabled" || status === "full" || status === "noGroup") {
                          let title = "This port is part of a different aggregation group.";
                          if (status === "full") title = "Maximum ports reached.";
                          if (status === "noGroup") title = "Maximum groups reached.";
                          return (
                            <Tooltip
                              key={portIdx}
                              arrow
                              title={title}
                            >
                              <span style={{ display: "inline-block" }}>
                                {renderBox}
                              </span>
                            </Tooltip>
                          );
                        }
                        return renderBox;
                        });
                      })()}
                    </Box>
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="caption" sx={{ color: "orange" }}>
                        All selected ports will be synchronized with these settings.
                      </Typography>
                    </Box>
                    {/* Aggregating Mode Dropdown */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1.5 }}>
                      <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Aggregating Mode</Typography>
                      <MenuSelect
                        disabled={!showEditGeneral || !isEditable}
                        value={(activeRow.operation?.aggregateType || "lacp").toLowerCase()}
                        onChange={(opt) => {
                          setActiveRow((prev) => ({
                            ...prev,
                            operation: {
                              ...prev.operation,
                              aggregateType: opt.value.toLowerCase()
                            }
                          }));
                        }}
                        options={[
                          { label: "LACP", value: "lacp" },
                          { label: "Static", value: "static" }
                        ]}
                        width="150px"
                      />
                    </Box>
                    {/* Group ID */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <Typography sx={{ minWidth: "110px", fontSize: 13, color: "text.secondary" }}>Group ID</Typography>
                      {activeRow.operation?.aggregateGroupId != null ? (
                        <Typography sx={{ fontSize: 14, fontWeight: "bold" }}>
                          {activeRow.operation.aggregateGroupId}
                        </Typography>
                      ) : (
                        <Typography sx={{ fontSize: 14, color: "error.main" }}>
                          Maximum groups reached.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </Box>
        </CollapseCard>

        {
          isSelectedPortSupportPoE &&
          <CollapseCard title="PoE">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  PoE Status
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.isPoeScheduleMode ? "schedule" : selectedProfile?.poeState ? "enable" : "disable"}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => (
                      <RadioButton
                        key={"profilePoeStatusRadioButton" + idx}
                        label={option}
                        name="profilePoeStatusRadio"
                        value={option.toLowerCase()}
                        disabled
                      />
                    ))}
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={activeRow?.isPoeScheduleMode ? "schedule" : activeRow?.poeState ? "enable" : "disable"}
                    onChange={(e) => {
                      setActiveRow((prev) => ({
                        ...prev,
                        poeState: e.target.value === "enable",
                        isPoeScheduleMode: e.target.value === "schedule"
                      }));
                    }}
                    sx={{
                      display: "flex",
                      gap: "12px",
                    }}
                  >
                    {["Enable", "Disable", "Schedule"].map((option, idx) => (
                      <RadioButton
                        key={"poeStatusRadioButton" + idx}
                        label={option}
                        name="poeStatusRadio"
                        value={option.toLowerCase()}
                        disabled={!showEditGeneral || !isEditable}
                      />
                    ))}
                  </RadioGroup>
                }
              </Box>
              {isProfileMode && selectedProfile?.isPoeScheduleMode && (
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}></Typography>
                  <MenuSelect
                    disabled
                    value={selectedProfile?.poeSchedule || ""}
                    options={scheduleOptions}
                    width="100%"
                  />
                </Box>
              )}
              {!isProfileMode && activeRow?.isPoeScheduleMode && (
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}></Typography>
                  <MenuSelect
                    disabled={!showEditGeneral || !isEditable}
                    value={activeRow?.poeSchedule || ""}
                    onChange={(opt) => {
                      if (opt.value === "Create New Schedule") {
                        setLeaveModalState({ open: true, profileType: "Schedule", page: "schedule" });
                        return;
                      }
                      setActiveRow((prev) => ({
                        ...prev,
                        poeSchedule: opt.value,
                      }));
                    }}
                    options={scheduleOptions}
                    placeholder="Select a PoE Profile"
                    width="100%"
                    pinningOptions={[
                      {
                        value: "Create New Schedule",
                        label: "Create New Schedule",
                        showArrow: true,
                      }
                    ]}
                  />
                </Box>
              )}
            </Box>
          </CollapseCard>
        }
        <CollapseCard title="Advance">
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={rowStyle}>
              <Typography className={mainStyle["collapse-typography"]}>
                DDP
              </Typography>

              {
                isProfileMode &&
                <RadioGroup
                  disabled
                  row
                  value={selectedProfile?.ddpState ? "enable" : "disable"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"profileDdpRadioButton" + idx}
                      label={option}
                      name="profileDdpRadio"
                      value={option.toLowerCase()}
                      disabled
                    />
                  ))}
                </RadioGroup>
              }

              {
                !isProfileMode &&
                <RadioGroup
                  row
                  value={activeRow?.ddpState ? "enable" : "disable"}
                  onChange={(e) => {
                    const newValue = e.target.value === "enable";
                    setActiveRow((prev) => ({
                      ...prev,
                      ddpState: newValue,
                    }));
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"ddpRadioButton" + idx}
                      label={option}
                      name="ddpRadio"
                      value={option.toLowerCase()}
                      disabled={!showEditGeneral || !isEditable}
                    />
                  ))}
                </RadioGroup>
              }
            </Box>

            <Box sx={rowStyle}>
              <Typography className={mainStyle["collapse-typography"]}>
                LBD
              </Typography>

              {
                isProfileMode &&
                <RadioGroup
                  disabled
                  row
                  value={selectedProfile?.lbdState ? "enable" : "disable"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"profileLbdRadioButton" + idx}
                      label={option}
                      name="profileLbdRadio"
                      value={option.toLowerCase()}
                      disabled
                    />
                  ))}
                </RadioGroup>
              }

              {
                !isProfileMode &&
                <RadioGroup
                  row
                  value={activeRow?.lbdState ? "enable" : "disable"}
                  onChange={(e) => {
                    const newValue = e.target.value === "enable";
                    setActiveRow((prev) => ({
                      ...prev,
                      lbdState: newValue,
                    }));
                  }}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"lbdRadioButton" + idx}
                      label={option}
                      name="lbdRadio"
                      value={option.toLowerCase()}
                      disabled={!showEditGeneral || !isEditable || activeRow?.rstpState === true}
                    />
                  ))}
                </RadioGroup>
              }
            </Box>

            <Box sx={rowStyle}>
              <Typography className={mainStyle["collapse-typography"]} sx={{ display: "flex", alignItems: "center" }}>
                RSTP
                <Tooltip title="When RSTP is enabled as the primary loop prevention mechanism, LBD should not be enabled." placement="top">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: 16,
                      ml: "5px",
                      "& img": {
                        content: `url(${icon_snmp})`,
                      },
                      "&:hover img": {
                        content: `url(${icon_snmp_hover})`,
                      },
                    }}
                  >
                    <img src={icon_snmp} alt="" style={{ width: 16, height: 16, cursor: "pointer" }} />
                  </Box>
                </Tooltip>
              </Typography>

              {
                isProfileMode &&
                <RadioGroup
                  disabled
                  row
                  value={selectedProfile?.rstpState ? "enable" : "disable"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => (
                    <RadioButton
                      key={"profileRstpRadioButton" + idx}
                      label={option}
                      name="profileRstpRadio"
                      value={option.toLowerCase()}
                      disabled
                    />
                  ))}
                </RadioGroup>
              }

              {
                !isProfileMode &&
                <RadioGroup
                  row
                  value={activeRow?.rstpState ? "enable" : "disable"}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "80px auto",
                    columnGap: "20px",
                  }}
                >
                  {["Enable", "Disable"].map((option, idx) => {
                    const isEnable = option.toLowerCase() === "enable";
                    return (
                      <RadioButton
                        key={"rstpRadioButton" + idx}
                        label={option}
                        name="rstpRadio"
                        value={option.toLowerCase()}
                        disabled={!showEditGeneral || !isEditable}
                        onClick={() => {
                          if (!showEditGeneral || !isEditable) return;
                          setActiveRow((prev) => ({
                            ...prev,
                            rstpState: isEnable,
                            ...(isEnable ? { lbdState: false } : { stpState: false }),
                          }));
                        }}
                      />
                    );
                  })}
                </RadioGroup>
              }
            </Box>

            {((isProfileMode ? selectedProfile?.rstpState : activeRow?.rstpState) === true) && (
              <Box sx={rowStyle}>
                <Typography className={mainStyle["collapse-typography"]}>
                  STP Guard
                </Typography>

                {
                  isProfileMode &&
                  <RadioGroup
                    disabled
                    row
                    value={selectedProfile?.stpState ? "enable" : "disable"}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => (
                      <RadioButton
                        key={"profileStpRadioButton" + idx}
                        label={option}
                        name="profileStpRadio"
                        value={option.toLowerCase()}
                        disabled
                      />
                    ))}
                  </RadioGroup>
                }

                {
                  !isProfileMode &&
                  <RadioGroup
                    row
                    value={activeRow?.stpState ? "enable" : "disable"}
                    onChange={(e) => {
                      const newValue = e.target.value === "enable";
                      setActiveRow((prev) => ({
                        ...prev,
                        stpState: newValue,
                      }));
                    }}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "80px auto",
                      columnGap: "20px",
                    }}
                  >
                    {["Enable", "Disable"].map((option, idx) => (
                      <RadioButton
                        key={"stpRadioButton" + idx}
                        label={option}
                        name="stpRadio"
                        value={option.toLowerCase()}
                        disabled={!showEditGeneral || !isEditable}
                      />
                    ))}
                  </RadioGroup>
                }
              </Box>
            )}

            {isProfileMode && selectedProfile?.portType === "access" && (
              <>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Access Policy
                    {isUnityProSubscribed || !selectedProfile?.accessPolicy?.enableState ? (
                      <ProColorIcon aria-label="Pro" style={proIconStyle} />
                    ) : (
                      <Tooltip
                        title="Pro feature disabled"
                        placement="bottom"
                        arrow
                      >
                        <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                      </Tooltip>
                    )}
                  </Typography>
                  <MenuSelect
                    disabled
                    value={
                      selectedProfile?.accessPolicy?.enableState
                        ? selectedProfile.accessPolicy.type
                        : accessPolicyTypeDefinition.DISABLED.value
                    }
                    options={defaultAccessPolicyTypeList}
                    width="100%"
                  />
                </Box>

                {selectedProfile?.accessPolicy?.enableState &&
                  (selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.STATIC.value ||
                    selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                    <MacWhitelistSection
                      macProfile={selectedProfile.accessPolicy.macList || null}
                      macProfileOptions={macProfiles.map((p) => ({ label: p.name, value: p.id, macCount: p.macList?.length ?? p.macAddresses?.length ?? 0 }))}
                      macProfilesRaw={macProfiles}
                      showEditGeneral={false}
                      showDynamicSize={selectedProfile.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                      dynamicSize={selectedProfile.accessPolicy.dynamicSize}
                      form={{
                        getTextfieldStyle: () => ({ width: "300px" }),
                        checkDynamicSize: () => true
                      }}
                      layout="drawer"
                    />
                  )}

                {selectedProfile?.accessPolicy?.enableState &&
                  selectedProfile.accessPolicy.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                    <RadiusProfileSection
                      radiusProfile={selectedProfile.accessPolicy.radiusProfile || null}
                      radiusProfileOptions={radiusProfiles.map((p) => ({ label: p.name, value: p.id }))}
                      radiusProfilesRaw={radiusProfiles}
                      showEditGeneral={false}
                      layout="drawer"
                    />
                  )}
              </>
            )}

            {!isProfileMode && activeRow?.portType === "access" && (
              <>
                <Box sx={rowStyle}>
                  <Typography className={mainStyle["collapse-typography"]}>
                    Access Policy
                    {isUnityProSubscribed || !activeRow?.accessPolicy?.enableState ? (
                      <ProColorIcon aria-label="Pro" style={proIconStyle} />
                    ) : (
                      <Tooltip
                        title="Pro feature disabled"
                        placement="bottom"
                        arrow
                      >
                        <ProOrangeOutlineIcon aria-label="Pro" style={proIconStyle} />
                      </Tooltip>
                    )}
                  </Typography>
                  <MenuSelect
                    disabled={!showEditGeneral || !isEditable}
                    value={
                      activeRow?.accessPolicy?.enableState
                        ? activeRow.accessPolicy.type
                        : accessPolicyTypeDefinition.DISABLED.value
                    }
                    onChange={(opt) => {
                      setActiveRow((prev) => ({
                        ...prev,
                        accessPolicy: {
                          ...prev.accessPolicy,
                          enableState: opt.value !== accessPolicyTypeDefinition.DISABLED.value,
                          type: opt.value === accessPolicyTypeDefinition.DISABLED.value
                            ? accessPolicyTypeDefinition.STATIC.value
                            : opt.value,
                        },
                      }));
                    }}
                    options={defaultAccessPolicyTypeList}
                    width="100%"
                  />
                </Box>

                {activeRow?.accessPolicy?.enableState &&
                  (activeRow.accessPolicy.type === accessPolicyTypeDefinition.STATIC.value ||
                    activeRow.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value) && (
                    <MacWhitelistSection
                      macProfile={activeRow.accessPolicy.macList || null}
                      onChangeMacProfile={(opt) => {
                        if (opt.value === "Edit/Create New Profile") {
                          setLeaveModalState({ open: true, profileType: "Mac Address", page: "mac-address" });
                          return;
                        }
                        setActiveRow((prev) => ({
                          ...prev,
                          accessPolicy: {
                            ...prev.accessPolicy,
                            macList: opt.value,
                          },
                        }));
                      }}
                      macProfileOptions={macProfiles.map((p) => ({ label: p.name, value: p.id, macCount: p.macList?.length ?? p.macAddresses?.length ?? 0 }))}
                      macProfilesRaw={macProfiles}
                      showEditGeneral={showEditGeneral}
                      showDynamicSize={activeRow.accessPolicy.type === accessPolicyTypeDefinition.DELETE_ON_TIME.value}
                      dynamicSize={activeRow.accessPolicy.dynamicSize}
                      onChangeDynamicSize={(val) => {
                        setActiveRow((prev) => ({
                          ...prev,
                          accessPolicy: {
                            ...prev.accessPolicy,
                            dynamicSize: val === "" ? null : parseInt(val, 10),
                          },
                        }));
                      }}
                      form={{
                        getTextfieldStyle: (isValid) => isValidInputStyles(isValid),
                        checkDynamicSize: (val) => {
                          if (val === "" || val == null) return false;
                          const num = Number(val);
                          return !isNaN(num) && num >= 1 && num <= 32;
                        }
                      }}
                      layout="drawer"
                    />
                  )}

                {activeRow?.accessPolicy?.enableState &&
                  activeRow.accessPolicy.type === accessPolicyTypeDefinition["8021X_PROFILE"].value && (
                    <RadiusProfileSection
                      radiusProfile={activeRow.accessPolicy.radiusProfile || null}
                      onChangeRadiusProfile={(opt) => {
                        if (opt.value === "Edit/Create New Profile") {
                          setLeaveModalState({ open: true, profileType: "RADIUS Server", page: "radius-server" });
                          return;
                        }
                        setActiveRow((prev) => ({
                          ...prev,
                          accessPolicy: {
                            ...prev.accessPolicy,
                            radiusProfile: opt.value,
                          },
                        }));
                      }}
                      radiusProfileOptions={radiusProfiles.map((p) => ({ label: p.name, value: p.id }))}
                      radiusProfilesRaw={radiusProfiles}
                      showEditGeneral={showEditGeneral}
                      layout="drawer"
                    />
                  )}
              </>
            )}
          </Box>
        </CollapseCard>
        {isChanged && (
          <Box
            sx={{
              position: "sticky",
              width: "107%",
              display: "flex",
              justifyContent: "center",
              padding: "16px",
              backgroundColor: "primary.lightBlue",
              bottom: 0,
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <OutlineButton
              onClick={handleCancel}
              sx={{ marginRight: "20px" }}
              width="100px"
              height="32px"
            >
              Revert
            </OutlineButton>
            <SolidButton
              disabled={!checkFormValid()}
              onClick={handleSaveClick}
              width="100px"
              height="32px"
            >
              Save
            </SolidButton>
          </Box>
        )}
      </Box>

      <AggregationConfirmModal
        showAggregationConfirmModal={showAggregationConfirmModal}
        setShowAggregationConfirmModal={setShowAggregationConfirmModal}
        onProceedCallback={() => onSave()}
      />
      <LeaveConfirmModal
        isOpen={leaveModalState.open}
        onClose={() => setLeaveModalState(prev => ({ ...prev, open: false }))}
        leaveCallback={handleLeaveConfirm}
        profileType={leaveModalState.profileType}
      />
      <LinkModal
        open={isLinkModalOpen}
        transparentBackdrop
        onClose={() => setIsLinkModalOpen(false)}
        onLinkClick={() => {
          setIsLinkModalOpen(false);
          const isMspScope = auth?.realm?.hasManageOrg === true && !auth?.orgs?.id;
          navigate(isMspScope ? "/msp/license" : "/license");
        }}
      />
    </>
  );
};

export default SettingsDrawer;
